import { m } from "framer-motion";
import { ArrowRight, Recycle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import heroImage from "@/assets/hero-recycling.webp";

// Image sizes for responsive loading
const HERO_IMAGE_SIZES = "(min-width: 1280px) 1920px, (min-width: 1024px) 1280px, (min-width: 768px) 1024px, 100vw";

const Hero = () => {
  const { t } = useTranslation();
  const { localePath } = useLanguage();

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      <div className="absolute inset-0">
        {/* Using picture element for better control over image loading */}
        <picture>
          <source srcSet={heroImage} type="image/webp" />
          <img
            src={heroImage}
            alt="Recyklační technika EkoKompakt — lisovací zařízení"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.3 }}
            loading="eager"
            fetchPriority="high"
            sizes={HERO_IMAGE_SIZES}
            width="1920"
            height="1080"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      </div>
      <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px] animate-glow-pulse" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-3xl">
          <m.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Recycle className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">{t("hero.badge")}</span>
          </m.div>

          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            {t("hero.title1")}{" "}
            <span className="text-gradient">{t("hero.titleHighlight")}</span>{" "}
            {t("hero.title2")}
          </h1>

          <m.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="text-lg sm:text-xl text-muted-foreground max-w-xl mb-8">
            {t("hero.subtitle")}
          </m.p>

          <m.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4">
            <a href="#services">
              <Button variant="hero" size="xl">
                {t("hero.cta1")}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </a>
            <a href="#contact">
              <Button variant="heroOutline" size="xl">
                {t("hero.cta2")}
              </Button>
            </a>
          </m.div>

          <m.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-border">
            {[
              { value: t("hero.stat1value"), label: t("hero.stat1label") },
              { value: t("hero.stat2value"), label: t("hero.stat2label") },
              { value: t("hero.stat3value"), label: t("hero.stat3label") },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="font-heading text-3xl sm:text-4xl font-bold text-primary">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </m.div>
        </div>
      </div>

      <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
          <m.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full bg-primary" />
        </div>
      </m.div>
    </section>
  );
};

export default Hero;
